import api from '../../api/apiClient';
import { ApiEnvelope, createResourceClient, unwrapEnvelope } from '../../api/resource';

/* ── Shared shapes ───────────────────────────────────────────────────────── */

/** Rows of the ten V5 lookup tables serialize with these fields. */
export interface LookupRow {
  id: number;
  code: string;
  label?: string;
  name?: string;
  description?: string | null;
  isActive?: boolean;
  sortOrder?: number | null;
  [key: string]: unknown;
}

export interface Department {
  id: number;
  name: string;
  description?: string | null;
}

/** Rows of the five point-of-contact master tables (`/api/poc/*`). */
export interface PocRow {
  id: number;
  name?: string;
  mobile?: string | null;
  phone?: string | null;
  whatsapp?: string | null;
  email?: string | null;
  address?: string | null;
  officeAddress?: string | null;
  [key: string]: unknown;
}

/**
 * A row of the `users` table — the login account, not the employee record.
 * `password` is write-only: the API never returns it, and the edit form only
 * sends it when the admin actually types a new one.
 */
export interface UserRow {
  id: number;
  name?: string;
  username?: string;
  employeenumber?: string | null;
  department?: string | null;
  mobile?: string | null;
  email?: string | null;
  role?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
  [key: string]: unknown;
}

export interface AdminEmployee {
  id: number;
  employeeId?: string;
  position?: string;
  joiningDate?: string;
  currentSalary?: number;
  dateOfBirth?: string;
  gender?: string;
  nationality?: string;
  [key: string]: unknown;
}

export interface JobQuote {
  id: number;
  /** V26 — bills are raised only from an approved quotation, which is then not edited. */
  approved?: boolean;
  approvedAt?: string | null;
  approvedByName?: string | null;
  /**
   * V36 — the client's work order, added by Accounts or the admin. The admin approves only
   * once it is there; the quotation PDF prints it.
   */
  workOrderNo?: string | null;
  workOrderDate?: string | null;
  workOrderAt?: string | null;
  workOrderByName?: string | null;
  /** V13 — the basis this quotation was priced on, and its own note. */
  quoteAsPer?: string | null;
  notes?: string | null;
  /**
   * V18 — what section B of the offer letter says is being quoted, and whether its
   * site-visit note prints per-visit rates or "Not in Scope". Both are client-facing;
   * `notes` is not.
   */
  scopeDescription?: string | null;
  siteVisitInScope?: boolean | null;
  /** V25 — 'INCLUDING' or 'EXCLUDING' all expenses; null on older quotations. */
  siteVisitExpenses?: 'INCLUDING' | 'EXCLUDING' | null;
  /** V35 — the date on the letter (null on older quotations: they carry their raise date), and its addressee. */
  quoteDate?: string | null;
  quoteTo?: 'CLIENT' | 'ARCHITECT' | 'BUILDER' | 'OWNER' | null;
  createdAt?: string | null;
  jobArea?: number;
  quotePercent?: number;
  constructionCost?: number;
  discount?: number;
  totalCost?: number;
  totalCostAfterDiscount?: number;
  grossTotal?: number;
  [key: string]: unknown;
}

export interface JobFloor {
  id: number;
  floorCount?: number;
  /** `existing` row only (V17): the floor the new work starts at. */
  startingFloorNo?: number | null;
  drawingsCount?: number;
  usageType?: string;
  variant?: string | null;
  isMezzanine?: boolean;
  [key: string]: unknown;
}

/** A `companies` row as the API serializes it (camelCase). */
export interface CompanyRow {
  id: number;
  name: string;
  address?: string | null;
  contactNo1?: string | null;
  contactNo2?: string | null;
  email?: string | null;
  website?: string | null;
  gstNo?: string | null;
  registrationNo?: string | null;
  city?: string | null;
  state?: string | null;
  country?: string | null;
  isActive?: boolean;
  [key: string]: unknown;
}

/* ── CRUD resources (identical route shape across all of these) ──────────── */

export const departmentsApi = createResourceClient<Department>('/api/departments');
/** V19 — the firm's own companies. Anyone signed in reads; only ADMIN writes. */
export const companiesApi = createResourceClient<CompanyRow>('/api/companies');
export const adminEmployeesApi = createResourceClient<AdminEmployee>('/api/admin/employees');
/** Admin-only; the backend rejects non-ADMIN callers on the whole tree with a 403. */
export const usersApi = createResourceClient<UserRow>('/api/users');
export const jobQuotesApi = createResourceClient<JobQuote>('/api/job-quotes');
export const jobFloorsApi = createResourceClient<JobFloor>('/api/job-floors');

/**
 * Reference tables from `V5__structural_normalization.sql`, plus the hiring pair added
 * by `V7__separate_hiring_and_project_type_lookups.sql`. The project lookups classify a
 * work job (New/Existing, Allied/Building); the hiring lookups classify a job posting
 * (Full-Time/Contract, On Site/Remote). They are separate on purpose.
 */
export const lookupApis = {
  projectTypes: createResourceClient<LookupRow>('/api/project-types'),
  constructionWorkTypes: createResourceClient<LookupRow>('/api/construction-work-types'),
  employmentTypes: createResourceClient<LookupRow>('/api/employment-types'),
  workArrangements: createResourceClient<LookupRow>('/api/work-arrangements'),
  buildingTypes: createResourceClient<LookupRow>('/api/building-types'),
  // V12 — replaces the free-text `locality` column on work_jobs.
  localities: createResourceClient<LookupRow>('/api/localities'),
  floorTypes: createResourceClient<LookupRow>('/api/floor-types'),
  documentCategories: createResourceClient<LookupRow>('/api/document-categories'),
  priorities: createResourceClient<LookupRow>('/api/priorities'),
  shiftTypes: createResourceClient<LookupRow>('/api/shift-types'),
  sanctioningAuthorities: createResourceClient<LookupRow>('/api/sanctioning-authorities'),
  statusCategories: createResourceClient<LookupRow>('/api/status-categories'),
  statuses: createResourceClient<LookupRow>('/api/statuses'),
  financialYears: createResourceClient<LookupRow>('/api/financial-years'),
  // V8 hiring masters — the qualification a request asks for and the tools it needs.
  qualifications: createResourceClient<LookupRow>('/api/qualifications'),
  tools: createResourceClient<LookupRow>('/api/tools'),
  // V21 — what kind of work a task is, and its types (each type belongs to one activity).
  workActivities: createResourceClient<LookupRow>('/api/work-activities'),
  // V24 — what time logged outside a task was spent on (Meeting, Training, …).
  timeEntryCategories: createResourceClient<LookupRow>('/api/time-entry-categories'),
  typeOfWorkActivities: createResourceClient<LookupRow>('/api/type-of-work-activities'),
} as const;

export type LookupKey = keyof typeof lookupApis;

/**
 * Point-of-contact masters behind `PocContactController`. They are keyed off
 * `/api/poc/{type}` rather than `/api/{resource}`, but the route shape and the
 * `{ success, data }` envelope are the same, so the standard client applies.
 */
export const pocApis = {
  architects: createResourceClient<PocRow>('/api/poc/architects'),
  builders: createResourceClient<PocRow>('/api/poc/builders'),
  owners: createResourceClient<PocRow>('/api/poc/owners'),
  supervisors: createResourceClient<PocRow>('/api/poc/supervisors'),
} as const;

export type PocKey = keyof typeof pocApis;

/* ── Document Master (V36) ─────────────────────────────────────────────── */

/** One company's prefix for one kind of document, and where its series starts. */
export interface DocumentPrefixRow {
  id: number | null;
  companyId: number;
  docType: 'QUOTATION' | 'PROFORMA' | 'INVOICE' | 'ADVANCE_RECEIPT';
  prefix: string;
  startNumber: number;
}

export const documentPrefixesApi = {
  /** Every document type, defaults filled in where nothing is saved yet. */
  list: async (companyId: number): Promise<DocumentPrefixRow[]> => {
    const res = await api.get<ApiEnvelope<DocumentPrefixRow[]>>(`/api/companies/${companyId}/document-prefixes`);
    return unwrapEnvelope(res.data) ?? [];
  },
  /** Admin only. */
  save: async (companyId: number, rows: DocumentPrefixRow[]): Promise<DocumentPrefixRow[]> => {
    const res = await api.put<ApiEnvelope<DocumentPrefixRow[]>>(`/api/companies/${companyId}/document-prefixes`, {
      rows: rows.map(({ docType, prefix, startNumber }) => ({ docType, prefix: prefix.trim(), startNumber })),
    });
    return unwrapEnvelope(res.data) ?? [];
  },
};

/* ── Job quotes / floors: extra lookups scoped to a work job ─────────────── */

/**
 * A job's quotations, newest first.
 *
 * Returns a list since V13, where a job gained the ability to hold more than one — each
 * save from the QUOTATION panel raises a new one and the newest is the quote in force.
 * An empty array means the job has no quotation yet, which is not an error.
 */
export async function getJobQuotesForJob(jobId: number | string): Promise<JobQuote[]> {
  const res = await api.get<ApiEnvelope<JobQuote[]>>(`/api/job-quotes/job/${jobId}`);
  return unwrapEnvelope(res.data) ?? [];
}

/** Approves a quotation (admin only, once it has a work order — V36) so bills can be raised from it, or withdraws that. */
export async function setJobQuoteApproval(quoteId: number, approve: boolean): Promise<JobQuote> {
  const res = await api.post<ApiEnvelope<JobQuote>>(`/api/job-quotes/${quoteId}/${approve ? 'approve' : 'unapprove'}`);
  return unwrapEnvelope(res.data) as JobQuote;
}

/**
 * Adds (or corrects, until approval) the client's work order on a quotation — Accounts or the
 * admin (V36). Every admin is then notified that it is waiting for approval.
 */
export async function setJobQuoteWorkOrder(
  quoteId: number,
  workOrderNo: string,
  workOrderDate: string | null
): Promise<JobQuote> {
  const res = await api.post<ApiEnvelope<JobQuote>>(`/api/job-quotes/${quoteId}/work-order`, { workOrderNo, workOrderDate });
  return unwrapEnvelope(res.data) as JobQuote;
}

/** One quotation waiting for the admin's approval: it has a work order and is not yet approved (V36). */
export interface QuoteAwaitingApproval {
  quoteId: number;
  jobId: number;
  jobCode: string;
  jobName: string;
  clientType: 'BUILDER' | 'ARCHITECT' | 'OWNER' | null;
  clientName: string | null;
  companyName: string | null;
  quoteDate: string | null;
  quoteAsPer: string | null;
  quoteValue: number;
  grossTotal: number | null;
  workOrderNo: string;
  workOrderDate: string | null;
  workOrderAt: string | null;
  workOrderByName: string | null;
  /** Whether it is the job's quotation in force (its newest). */
  current: boolean;
}

/** The admin's approval queue, newest work order first. */
export async function getQuotesAwaitingApproval(): Promise<QuoteAwaitingApproval[]> {
  const res = await api.get<ApiEnvelope<QuoteAwaitingApproval[]>>('/api/job-quotes/awaiting-approval');
  return unwrapEnvelope(res.data) ?? [];
}

/** Raises a new quotation against a job. Appends — it never overwrites an earlier one. */
export async function createJobQuote(
  jobId: number | string,
  payload: Record<string, unknown>
): Promise<JobQuote | null> {
  const res = await api.post<ApiEnvelope<JobQuote>>('/api/job-quotes', {
    job_id: jobId,
    ...payload,
  });
  return unwrapEnvelope(res.data) ?? null;
}

/** A fetched quotation PDF: an object URL to show it by, and the name to save it under. */
export interface QuotationPdfFile {
  /** `blob:` URL. The caller owns it and must revoke it when done — see below. */
  url: string;
  filename: string;
}

/** Reads the filename the server chose, falling back to a plain default. */
function quotationFilenameFrom(disposition: unknown): string {
  const match = typeof disposition === 'string' ? /filename="?([^"]+)"?/.exec(disposition) : null;
  return match?.[1] ?? 'quotation.pdf';
}

/**
 * The quotation's offer letter, as a PDF, ready to display.
 *
 * Fetched rather than linked: `/api/job-quotes/{id}/pdf` is behind the same bearer token
 * as everything else, and a plain `<iframe src>` or `window.open` of it sends no
 * Authorization header and comes back 401. So the blob is fetched through the configured
 * client and shown from an object URL.
 *
 * Returns the URL rather than opening anything — where the letter is shown is the screen's
 * decision, and the quotation list shows it in a viewer on the page. **The caller must
 * `URL.revokeObjectURL(url)` once it is done**, or the blob is held until the tab closes.
 *
 * `withLetterhead: false` fetches the version for the firm's pre-printed stationery: no
 * letterhead, header or watermark, with their space left blank.
 */
export async function fetchJobQuotePdf(
  quoteId: number | string,
  withLetterhead = true
): Promise<QuotationPdfFile> {
  const res = await api.get<Blob>(`/api/job-quotes/${quoteId}/pdf`, {
    responseType: 'blob',
    params: withLetterhead ? undefined : { letterhead: false },
  })
    .catch(async (error: any) => {
      // A failed PDF still arrives as a Blob, so the interceptor never saw the JSON error
      // body. Read it here or the user gets a bare "Network Error".
      const body = error?.response?.data;
      if (body instanceof Blob) {
        try {
          const parsed = JSON.parse(await body.text());
          error.userMessage = parsed?.message || parsed?.error || error.userMessage;
        } catch {
          // Not JSON after all — leave the interceptor's message in place.
        }
      }
      throw error;
    });

  return {
    // Typed explicitly: an object URL the browser will not treat as a PDF opens as a
    // download prompt instead of rendering in the viewer.
    url: URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' })),
    filename: quotationFilenameFrom(res.headers?.['content-disposition']),
  };
}

export async function getJobFloorsForJob(jobId: number | string): Promise<JobFloor[]> {
  const res = await api.get<ApiEnvelope<JobFloor[]>>(`/api/job-floors/job/${jobId}`);
  return unwrapEnvelope(res.data) ?? [];
}

/**
 * Replaces a job's whole floor set in one request.
 *
 * The Type of Work screen describes a building as a whole rather than editing rows one by
 * one, so there is no per-row id to PUT against. Posting the rows individually would also
 * collide with `job_floors_job_type_variant_unique` on the second save, which is why the
 * server clears the old set first.
 */
/**
 * `totalAreaSqFt` is not a floor — it lives on the job — but it is collected on the same
 * screen and saved by the same button, so it travels with the floors rather than in a
 * second request that could fail on its own and leave the two out of step.
 */
export async function replaceJobFloorsForJob(
  jobId: number | string,
  floors: unknown[],
  totalAreaSqFt?: number | string | null
): Promise<JobFloor[]> {
  const res = await api.put<ApiEnvelope<JobFloor[]>>(`/api/job-floors/job/${jobId}`, {
    floors,
    ...(totalAreaSqFt === undefined ? {} : { total_area_sqft: totalAreaSqFt }),
  });
  return unwrapEnvelope(res.data) ?? [];
}

/* ── Statuses filtered by category (`?category=work_job` etc.) ───────────── */

export async function getStatusesByCategory(categoryCode: string): Promise<LookupRow[]> {
  const res = await api.get<ApiEnvelope<LookupRow[]>>('/api/statuses', {
    params: { category: categoryCode },
  });
  return unwrapEnvelope(res.data) ?? [];
}

/* ── Financial years ─────────────────────────────────────────────────────── */

export async function getActiveFinancialYear(): Promise<LookupRow | null> {
  const res = await api.get<ApiEnvelope<LookupRow>>('/api/financial-years/active');
  return unwrapEnvelope(res.data) ?? null;
}

export async function activateFinancialYear(id: number | string): Promise<void> {
  await api.put(`/api/financial-years/${id}/activate`);
}

/* ── Roles (`RoleController` — not a standard CRUD resource) ─────────────── */

export interface RoleRow {
  id?: number;
  name: string;
  users?: Array<{ id: number; name?: string; username?: string }>;
  [key: string]: unknown;
}

export async function getRoles(): Promise<RoleRow[]> {
  const res = await api.get<ApiEnvelope<RoleRow[]> | RoleRow[]>('/api/roles');
  const body = unwrapEnvelope(res.data as any);
  return Array.isArray(body) ? body : [];
}

export async function createRole(name: string): Promise<void> {
  await api.post('/api/roles/create', { name });
}

export async function assignRole(role: string, userId: number | string): Promise<void> {
  await api.post('/api/roles/assign', { role, user_id: userId });
}

/* ── Dashboard ───────────────────────────────────────────────────────────── */

export async function getDashboard(): Promise<{ message?: string }> {
  const res = await api.get<{ message?: string }>('/api/dashboard');
  return res.data ?? {};
}
